import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import Layout from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { CaloriesCalculator } from '@/components/CaloriesCalculator';
import { triggerProfileUpdate } from '@/utils/onboardingEvents';

type Gender = 'male' | 'female';
type ActivityLevel = 'sedentary' | 'light' | 'moderate' | 'active' | 'very_active';
type Goal = 'lose' | 'maintain' | 'gain';

const mapActivityLevel = (dbActivityLevel: string): ActivityLevel => {
  switch(dbActivityLevel) {
    case 'sedentary': return 'sedentary';
    case 'light': return 'light';
    case 'moderate': return 'moderate'; 
    case 'active': return 'active';
    case 'very_active': return 'very_active';
    default: return 'moderate';
  }
};

const mapGender = (dbGender: string): Gender => {
  return dbGender === 'female' ? 'female' : 'male';
};

const mapGoal = (dbGoal: string): Goal => {
  switch(dbGoal) {
    case 'lose': return 'lose';
    case 'gain': return 'gain';
    default: return 'maintain';
  }
};

interface ProfileData {
  gender: Gender;
  age: number;
  weight: number;
  height: number;
  activity_level: string;
  goal: string;
  meals_per_day: number;
  target_calories?: number;
  target_protein?: number;
  target_carbs?: number;
  target_fat?: number;
  target_iron?: number;
  target_calcium?: number;
  target_zinc?: number;
  target_magnesium?: number;
  target_omega3?: number;
}

const Nutrition: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [profile, setProfile] = useState<ProfileData>({
    gender: 'male',
    age: 30,
    weight: 0,
    height: 170,
    activity_level: 'moderate',
    goal: 'maintain',
    meals_per_day: 3
  });
  const [useSimpleForm, setUseSimpleForm] = useState(true);
  const weightSyncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const weightUpdateInProgressRef = useRef(false);

  useEffect(() => {
    loadProfile();
    
    // Check if we have a temporary weight stored from navigation
    if (user?.id) {
      try {
        const tempWeight = localStorage.getItem('temp_weight_' + user.id);
        if (tempWeight) {
          const weightValue = parseFloat(tempWeight);
          if (!isNaN(weightValue) && weightValue > 0) {
            console.log('Found temporary weight from navigation:', weightValue);
            setProfile(prev => ({ ...prev, weight: weightValue }));
            // Also save this weight to Supabase immediately
            updateWeightInSupabase(weightValue);
          }
          // Clear the temporary storage
          localStorage.removeItem('temp_weight_' + user.id);
        }
      } catch (error) {
        console.error('Error loading temporary weight:', error);
      }
    }
  }, [user?.id]);

  const loadProfile = async () => {
    if (!user?.id) return;
    
    try {
      setLoading(true);
      console.log('Loading profile for user:', user.id);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('gender, age, weight, height, activity_level, goal, meals_per_day, target_calories, target_protein, target_carbs, target_fat, target_iron, target_calcium, target_zinc, target_magnesium, target_omega3')
        .eq('id', user.id)
        .single();

      if (error) {
        console.error('Error loading profile:', error);
        throw error;
      }
      
      if (data) {
        console.log('Loaded profile data:', data);
        
        // Store weight in localStorage for backup and synchronization
        if (data.weight && data.weight > 0) {
          try {
            localStorage.setItem('user_weight_' + user.id, String(data.weight));
            console.log('Stored weight in localStorage:', data.weight);
          } catch (localStorageError) {
            console.error('Error storing weight in localStorage:', localStorageError);
          }
        }
        
        const profileData = {
          ...data,
          weight: data.weight && data.weight > 0 ? data.weight : 0
        };
        
        setProfile(profileData);
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error("Error al cargar tu perfil nutricional");
    } finally {
      setLoading(false);
    }
  };

  const updateWeightInSupabase = async (weight: number) => {
    if (!user?.id || user.id.startsWith('user-') || weightUpdateInProgressRef.current) return;
    
    // Prevent concurrent updates
    weightUpdateInProgressRef.current = true;
    
    try {
      console.log(`Updating weight to ${weight}kg in Supabase directly`);
      
      const { error } = await supabase
        .from('profiles')
        .update({ weight })
        .eq('id', user.id);
        
      if (error) {
        console.error('Error updating weight in Supabase:', error);
        toast.error('Error al actualizar el peso');
        return;
      }
      
      // Update localStorage
      localStorage.setItem('user_weight_' + user.id, String(weight));
      
      // Broadcast the update to other components with a fixed weight value
      const event = new CustomEvent('weight_updated', {
        detail: {
          userId: user.id,
          weight,
          timestamp: Date.now()
        }
      });
      window.dispatchEvent(event);
      
      // Also dispatch profile update for components using that event
      const profileEvent = new CustomEvent('NUTRITION_PROFILE_UPDATED', {
        detail: { 
          profile: { ...user.profile, weight },
          weightUpdated: true
        }
      });
      window.dispatchEvent(profileEvent);
      
      console.log('Successfully updated weight in Supabase and broadcasted events');
      
      // Force refresh any cached data that might be using the old weight
      const forceRefreshEvent = new Event('FORCE_REFRESH_DATA');
      window.dispatchEvent(forceRefreshEvent);
      
    } catch (error) {
      console.error('Error in updateWeightInSupabase:', error);
    } finally {
      weightUpdateInProgressRef.current = false;
    }
  };

  const handleSave = async () => {
    if (!user?.id) {
      console.error('No user ID available, cannot save profile');
      return;
    }
    
    try {
      setSaving(true);
      console.log('Saving profile for user:', user.id);
      
      const validatedProfile = {
        gender: profile.gender,
        age: Math.max(1, Number(profile.age) || 30),
        weight: Math.max(0, Number(profile.weight) || 0),
        height: Math.max(1, Number(profile.height) || 170),
        activity_level: profile.activity_level,
        goal: profile.goal,
        meals_per_day: Math.max(1, Number(profile.meals_per_day) || 3)
      };
      
      const calculationWeight = validatedProfile.weight > 0 ? validatedProfile.weight : 
        Math.round((validatedProfile.height - 100) * 0.9);
      
      const defaultIron = validatedProfile.gender === 'female' ? 18 : 8; // mg
      const defaultCalcium = 1000; // mg
      const defaultZinc = 11; // mg
      const defaultMagnesium = 400; // mg
      const defaultOmega3 = 1.6; // g
      
      let baseCalories = 10 * calculationWeight + 6.25 * validatedProfile.height - 5 * validatedProfile.age;
      baseCalories = validatedProfile.gender === 'male' ? baseCalories + 5 : baseCalories - 161;
      
      const activityMultipliers = {
        sedentary: 1.2,
        light: 1.375,
        moderate: 1.55,
        active: 1.725,
        very_active: 1.9
      };
      
      const activityLevel = validatedProfile.activity_level as keyof typeof activityMultipliers;
      baseCalories *= activityMultipliers[activityLevel] || 1.55;
      
      const goalMultipliers = {
        lose: 0.8,
        maintain: 1,
        gain: 1.2
      };
      
      const goal = validatedProfile.goal as keyof typeof goalMultipliers;
      const calculatedCalories = Math.round(baseCalories * (goalMultipliers[goal] || 1));
      
      // ENFORCE MINIMUM 1500 CALORIES
      const targetCalories = Math.max(1500, calculatedCalories);
      
      if (calculatedCalories < 1500) {
        console.log(`🔒 NUTRITION SAVE: Enforcing minimum 1500 calories (calculated ${calculatedCalories})`);
      }
      
      const targetProtein = Math.round(validatedProfile.weight * (validatedProfile.goal === 'gain' ? 2 : 1.6));
      const targetFat = Math.round((targetCalories * 0.25) / 9);
      const targetCarbs = Math.round((targetCalories - (targetProtein * 4 + targetFat * 9)) / 4);
      
      const nutritionGoals = {
        targetCalories: Math.max(1500, targetCalories), // Double check minimum
        targetProtein: Math.max(10, targetProtein),
        targetCarbs: Math.max(10, targetCarbs),
        targetFat: Math.max(10, targetFat),
        targetIron: Math.max(1, defaultIron),
        targetCalcium: Math.max(100, defaultCalcium),
        targetZinc: Math.max(1, defaultZinc),
        targetMagnesium: Math.max(50, defaultMagnesium),
        targetOmega3: Math.max(0.1, defaultOmega3)
      };
      
      console.log('Calculated nutrition goals:', nutritionGoals);
      console.log('Saving weight to profile:', validatedProfile.weight);

      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          gender: validatedProfile.gender,
          age: validatedProfile.age,
          weight: validatedProfile.weight,
          height: validatedProfile.height,
          activity_level: validatedProfile.activity_level,
          goal: validatedProfile.goal,
          meals_per_day: validatedProfile.meals_per_day,
          target_calories: nutritionGoals.targetCalories,
          target_protein: nutritionGoals.targetProtein,
          target_carbs: nutritionGoals.targetCarbs,
          target_fat: nutritionGoals.targetFat,
          target_iron: nutritionGoals.targetIron,
          target_calcium: nutritionGoals.targetCalcium,
          target_zinc: nutritionGoals.targetZinc,
          target_magnesium: nutritionGoals.targetMagnesium,
          target_omega3: nutritionGoals.targetOmega3
        })
        .eq('id', user.id);

      if (profileError) {
        throw profileError;
      }

      // Store weight in localStorage for backup and synchronization
      localStorage.setItem('user_weight_' + user.id, String(validatedProfile.weight));

      triggerProfileUpdate();
      
      try {
        for (const key of Object.keys(localStorage)) {
          if (key.startsWith(`weeklyMenu_${user.id}`) || 
              key.startsWith('nutrition_') || 
              key.startsWith('journal_') ||
              key.startsWith('vitalScore_')) {
            console.log(`Clearing cached data: ${key}`);
            localStorage.removeItem(key);
          }
        }
        
        const timestamp = new Date().toISOString();
        localStorage.setItem('profile_updated', timestamp);
        
        localStorage.setItem('nutrition_goals_updated', JSON.stringify({
          userId: user.id,
          timestamp,
          goals: {
            calories: nutritionGoals.targetCalories,
            protein: nutritionGoals.targetProtein,
            carbs: nutritionGoals.targetCarbs,
            fat: nutritionGoals.targetFat,
            iron: nutritionGoals.targetIron,
            calcium: nutritionGoals.targetCalcium,
            zinc: nutritionGoals.targetZinc,
            magnesium: nutritionGoals.targetMagnesium,
            omega3: nutritionGoals.targetOmega3
          }
        }));
      } catch (error) {
        console.error('Error clearing cached data:', error);
      }
      
      const nutritionUpdateEvent = new CustomEvent('NUTRITION_PROFILE_UPDATED', {
        detail: {
          userId: user.id,
          timestamp: new Date().toISOString(),
          nutritionGoals: nutritionGoals,
          goals: {
            calories: nutritionGoals.targetCalories,
            protein: nutritionGoals.targetProtein,
            carbs: nutritionGoals.targetCarbs,
            fat: nutritionGoals.targetFat,
            iron: nutritionGoals.targetIron,
            calcium: nutritionGoals.targetCalcium,
            zinc: nutritionGoals.targetZinc,
            magnesium: nutritionGoals.targetMagnesium,
            omega3: nutritionGoals.targetOmega3
          },
          weight: validatedProfile.weight,
          weightUpdated: true
        }
      });
      window.dispatchEvent(nutritionUpdateEvent);
      
      const forceRefreshEvent = new Event('FORCE_REFRESH_DATA');
      window.dispatchEvent(forceRefreshEvent);
      
      const journalUpdateEvent = new CustomEvent('journal_goals_updated', {
        detail: {
          userId: user.id,
          goals: {
            calories: nutritionGoals.targetCalories,
            protein: nutritionGoals.targetProtein,
            carbs: nutritionGoals.targetCarbs,
            fat: nutritionGoals.targetFat,
            iron: nutritionGoals.targetIron,
            calcium: nutritionGoals.targetCalcium,
            zinc: nutritionGoals.targetZinc,
            magnesium: nutritionGoals.targetMagnesium,
            omega3: nutritionGoals.targetOmega3
          },
          weight: validatedProfile.weight
        }
      });
      window.dispatchEvent(journalUpdateEvent);
      
      const weightUpdateEvent = new CustomEvent('weight_updated', {
        detail: {
          userId: user.id,
          weight: validatedProfile.weight,
          timestamp: new Date().getTime()
        }
      });
      window.dispatchEvent(weightUpdateEvent);
      
      // Set save success state
      setSaveSuccess(true);
      
      // Reset the save success state after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
      
      // Always show confirmation toast when changes are saved successfully
      toast.success("Cambios guardados correctamente");
      
      const today = new Date();
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(today.getMonth() - 1);
      
      const { data: menusData, error: menusError } = await supabase
        .from('weekly_menus')
        .select('id, week')
        .eq('user_id', user.id)
        .gte('updated_at', oneMonthAgo.toISOString());
      
      if (menusError) {
        console.error('Error fetching menus:', menusError);
      } else if (menusData && menusData.length > 0) {
        console.log(`Found ${menusData.length} menus to update with new nutrition goals:`, nutritionGoals);
        
        for (const menu of menusData) {
          const { error: updateError } = await supabase
            .from('weekly_menus')
            .update({
              nutrition_goals: {
                calories: nutritionGoals.targetCalories,
                protein: nutritionGoals.targetProtein,
                carbs: nutritionGoals.targetCarbs,
                fat: nutritionGoals.targetFat,
                iron: nutritionGoals.targetIron,
                calcium: nutritionGoals.targetCalcium,
                zinc: nutritionGoals.targetZinc,
                magnesium: nutritionGoals.targetMagnesium,
                omega3: nutritionGoals.targetOmega3
              },
              updated_at: new Date().toISOString()
            })
            .eq('id', menu.id);
              
          if (updateError) {
            console.error(`Error updating menu ${menu.id}:`, updateError);
          } else {
            console.log(`Successfully updated menu ${menu.id} with new nutrition goals`);
          }
        }
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error("Error al guardar los cambios");
    } finally {
      setSaving(false);
    }
  };

  const handleCaloriesCalculatorSave = async (data: any) => {
    if (!user?.id) return;
    
    try {
      setSaving(true);
      console.log('Saving calculated profile data:', data);
      
      const updatedProfile = {
        gender: mapGender(data.gender),
        age: data.age,
        weight: data.weight,
        height: data.height,
        activity_level: mapActivityLevel(data.activityLevel),
        goal: mapGoal(data.goal),
        meals_per_day: data.mealsPerDay
      };
      
      setProfile(updatedProfile);
      
      const { error } = await supabase
        .from('profiles')
        .update({
          gender: updatedProfile.gender,
          age: data.age,
          weight: data.weight,
          height: data.height,
          activity_level: updatedProfile.activity_level,
          goal: updatedProfile.goal,
          meals_per_day: data.mealsPerDay,
          target_calories: data.targetCalories,
          target_protein: data.targetProtein,
          target_carbs: data.targetCarbs,
          target_fat: data.targetFat,
          target_iron: data.targetIron || (data.gender === 'female' ? 18 : 8),
          target_calcium: data.targetCalcium || 1000,
          target_zinc: data.targetZinc || 11,
          target_magnesium: data.targetMagnesium || 400,
          target_omega3: data.targetOmega3 || 1.6
        })
        .eq('id', user.id);
      
      if (error) throw error;
      
      triggerProfileUpdate();
      
      localStorage.removeItem(`weeklyMenu_${user.id}`);
      
      const today = new Date();
      const oneMonthAgo = new Date();
      oneMonthAgo.setMonth(today.getMonth() - 1);
      
      const { data: menusData, error: menusError } = await supabase
        .from('weekly_menus')
        .select('id, week')
        .eq('user_id', user.id)
        .gte('updated_at', oneMonthAgo.toISOString());
      
      if (menusError) {
        console.error('Error fetching menus:', menusError);
      } else if (menusData && menusData.length > 0) {
        console.log(`Found ${menusData.length} menus to update with new nutrition goals from calculator`);
        
        for (const menu of menusData) {
          await supabase
            .from('weekly_menus')
            .update({
              nutrition_goals: {
                calories: data.targetCalories,
                protein: data.targetProtein,
                carbs: data.targetCarbs,
                fat: data.targetFat,
                iron: data.targetIron || (data.gender === 'female' ? 18 : 8),
                calcium: data.targetCalcium || 1000,
                zinc: data.targetZinc || 11,
                magnesium: data.targetMagnesium || 400,
                omega3: data.targetOmega3 || 1.6
              },
              updated_at: new Date().toISOString()
            })
            .eq('id', menu.id);
        }
      }
      
      const timestamp = new Date().toISOString();
      localStorage.setItem('profile_updated', timestamp);
      localStorage.setItem('nutrition_goals_updated', JSON.stringify({
        userId: user.id,
        timestamp,
        goals: {
          calories: data.targetCalories,
          protein: data.targetProtein,
          carbs: data.targetCarbs,
          fat: data.targetFat,
          iron: data.targetIron || (data.gender === 'female' ? 18 : 8),
          calcium: data.targetCalcium || 1000,
          zinc: data.targetZinc || 11,
          magnesium: data.targetMagnesium || 400,
          omega3: data.targetOmega3 || 1.6
        }
      }));
      
      window.dispatchEvent(new CustomEvent('NUTRITION_PROFILE_UPDATED', {
        detail: {
          userId: user.id,
          timestamp,
          goals: {
            calories: data.targetCalories,
            protein: data.targetProtein,
            carbs: data.targetCarbs,
            fat: data.targetFat,
            iron: data.targetIron || (data.gender === 'female' ? 18 : 8),
            calcium: data.targetCalcium || 1000,
            zinc: data.targetZinc || 11,
            magnesium: data.targetMagnesium || 400,
            omega3: data.targetOmega3 || 1.6
          }
        }
      }));
      
      window.dispatchEvent(new Event('FORCE_REFRESH_DATA'));
      
      window.dispatchEvent(new CustomEvent('journal_goals_updated', {
        detail: {
          userId: user.id,
          goals: {
            calories: data.targetCalories,
            protein: data.targetProtein,
            carbs: data.targetCarbs,
            fat: data.targetFat,
            iron: data.targetIron || (data.gender === 'female' ? 18 : 8),
            calcium: data.targetCalcium || 1000,
            zinc: data.targetZinc || 11,
            magnesium: data.targetMagnesium || 400,
            omega3: data.targetOmega3 || 1.6
          }
        }
      }));
      
      toast.success("Perfil nutricional actualizado", {
        description: "Tus objetivos han sido recalculados correctamente."
      });
    } catch (error) {
      console.error('Error saving calculator data:', error);
      toast.error("Error al guardar los cambios");
    } finally {
      setSaving(false);
    }
  };

  const updateWeight = async (newWeight: number) => {
    if (!user?.id || user.id.startsWith('user-')) return;
    
    // Cancel any pending weight updates
    if (weightSyncTimeoutRef.current) {
      clearTimeout(weightSyncTimeoutRef.current);
    }
    
    weightSyncTimeoutRef.current = setTimeout(async () => {
      try {
        console.log(`Updating weight to ${newWeight}kg in Supabase`);
        
        // Update weight in profile table
        const { error } = await supabase
          .from('profiles')
          .update({ weight: newWeight })
          .eq('id', user.id);
          
        if (error) {
          console.error('Error updating weight:', error);
          toast.error('Error al actualizar tu peso');
          return;
        }
        
        // Store weight in localStorage as backup
        try {
          localStorage.setItem('user_weight_' + user.id, String(newWeight));
        } catch (localStorageError) {
          console.error('Error storing weight in localStorage:', localStorageError);
        }
        
        // Dispatch weight update event
        const weightEvent = new CustomEvent('weight_updated', {
          detail: {
            userId: user.id,
            weight: newWeight,
            timestamp: Date.now()
          }
        });
        window.dispatchEvent(weightEvent);
        
        console.log('Weight updated successfully:', newWeight);
      } catch (error) {
        console.error('Error in updateWeight:', error);
      }
    }, 300);
  };

  useEffect(() => {
    if (user?.id && profile.weight > 0) {
      // Update weight in localStorage
      try {
        const storedWeight = localStorage.getItem('user_weight_' + user.id);
        const newWeight = profile.weight;
        
        if (!storedWeight || Math.abs(parseFloat(storedWeight) - newWeight) > 0.01) {
          localStorage.setItem('user_weight_' + user.id, String(newWeight));
          updateWeight(newWeight);
        }
      } catch (error) {
        console.error('Error processing weight change:', error);
      }
    }
  }, [profile.weight, user?.id]);

  return (
    <Layout>
      <div className="container mx-auto p-4 md:p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold">Tus Características</h1>
        </div>

        <Card className="max-w-3xl mx-auto">
          <CardHeader>
            <CardTitle>Perfil Nutricional</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {useSimpleForm ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <Label htmlFor="gender">Género</Label>
                    <Select
                      value={profile.gender}
                      onValueChange={(value: Gender) => setProfile(prev => ({ ...prev, gender: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Masculino</SelectItem>
                        <SelectItem value="female">Femenino</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="age">Edad</Label>
                    <Input
                      id="age"
                      type="number"
                      value={profile.age}
                      onChange={(e) => setProfile(prev => ({ ...prev, age: parseInt(e.target.value) || 0 }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="weight">Peso (kg)</Label>
                    <Input
                      id="weight"
                      type="number"
                      step="0.1"
                      value={profile.weight || ''}
                      placeholder="Ingresa tu peso"
                      onChange={(e) => {
                        const newWeight = parseFloat(e.target.value) || 0;
                        setProfile(prev => ({ ...prev, weight: newWeight }));
                      }}
                      onBlur={(e) => {
                        const newWeight = parseFloat(e.target.value) || 0;
                        if (newWeight > 0) {
                          // Force immediate update to Supabase on blur
                          updateWeightInSupabase(newWeight);
                        }
                      }}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="height">Altura (cm)</Label>
                    <Input
                      id="height"
                      type="number"
                      value={profile.height}
                      onChange={(e) => setProfile(prev => ({ ...prev, height: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="activity">Nivel de Actividad</Label>
                    <Select
                      value={profile.activity_level}
                      onValueChange={(value: string) => setProfile(prev => ({ ...prev, activity_level: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sedentary">Sedentario</SelectItem>
                        <SelectItem value="light">Actividad Ligera</SelectItem>
                        <SelectItem value="moderate">Actividad Moderada</SelectItem>
                        <SelectItem value="active">Activo</SelectItem>
                        <SelectItem value="very_active">Muy Activo</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="goal">Objetivo</Label>
                    <Select
                      value={profile.goal}
                      onValueChange={(value: string) => setProfile(prev => ({ ...prev, goal: value }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="lose">Perder Peso</SelectItem>
                        <SelectItem value="maintain">Mantener Peso</SelectItem>
                        <SelectItem value="gain">Ganar Peso</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="meals">Comidas por Día</Label>
                    <Select
                      value={profile.meals_per_day.toString()}
                      onValueChange={(value) => setProfile(prev => ({ ...prev, meals_per_day: parseInt(value) }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">3 comidas</SelectItem>
                        <SelectItem value="4">4 comidas</SelectItem>
                        <SelectItem value="5">5 comidas</SelectItem>
                        <SelectItem value="6">6 comidas</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2">
                  <Button 
                    className={`w-full md:w-auto ${saveSuccess ? 'bg-green-500 hover:bg-green-600' : ''}`} 
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Guardando...' : saveSuccess ? 'Cambios guardados' : 'Guardar Cambios'}
                  </Button>
                  
                  {saveSuccess && (
                    <p className="text-sm text-green-600 mt-2">
                      Tus cambios se han guardado correctamente.
                    </p>
                  )}
                </div>
              </>
            ) : (
              <CaloriesCalculator 
                initialValues={{
                  gender: mapGender(profile.gender),
                  age: profile.age,
                  weight: profile.weight,
                  height: profile.height,
                  activityLevel: mapActivityLevel(profile.activity_level),
                  goal: mapGoal(profile.goal),
                  mealsPerDay: profile.meals_per_day
                }}
                onSave={handleCaloriesCalculatorSave} 
              />
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Nutrition;
